// CustomDataSourceEditDrawer.js

import React, { useEffect } from 'react';
import { Offcanvas, Form } from 'react-bootstrap'; //, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { FormProvider, useForm } from 'react-hook-form';
import DrawerWrapper, { DrawerFooter } from 'components/IAP/DrawerWrapper';
import FormTextField from 'components/IAP/formFields/FormTextField';
import FormKeyValuePairs from 'components/IAP/formFields/FormKeyValuePairs';
import { toastConfig } from '../../assets/data/config';
import { useTestCustomDataSource } from './hooks/useCustomDataSources';
import HeadersToolTipInfo from './HeadersToolTipInfo';
import HelpTooltip from 'components/DesignSystems/ToolTips/HelpTooltip';

const CustomDataSourceEditDrawer = ({ onHide, onSave, dataSource }) => {
  const methods = useForm({
    mode: 'onChange', // Validate the form on every change
    defaultValues: {
      id: '',
      name: '',
      description: '',
      url: '',
      method: 'GET',
      key_value_pairs: [],
      header_key_value_pairs: [],
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = methods;

  const testCustomDataSourceMutation = useTestCustomDataSource();
  const { data: testResult, isLoading, mutate } = testCustomDataSourceMutation;

  useEffect(() => {
    if (dataSource) {
      reset({
        id: dataSource.id || '',
        name: dataSource.name || '',
        description: dataSource.description || '',
        url: dataSource.url || '',
        method: dataSource.method || 'GET',
        key_value_pairs: Object.entries(dataSource.key_value_pairs || {}).map(
          ([key, value]) => ({
            key,
            value,
          })
        ),
        header_key_value_pairs: Object.entries(
          dataSource.header_key_value_pairs || {}
        ).map(([key, value]) => ({
          key,
          value,
        })),
      });
    } else {
      reset({
        id: '',
        name: '',
        description: '',
        url: '',
        method: 'GET',
        key_value_pairs: [],
        header_key_value_pairs: [],
      });
    }
  }, [dataSource, reset]);

  const handleSaveDataSource = (formData) => {
    try {
      const keyValuePairs = formData.key_value_pairs.reduce((acc, pair) => {
        acc[pair.key] = pair.value;
        return acc;
      }, {});

      const headerKeyValuePairs = formData.header_key_value_pairs.reduce(
        (acc, pair) => {
          acc[pair.key] = pair.value;
          return acc;
        },
        {}
      );

      const dataSourceData = {
        id: formData.id,
        name: formData.name,
        description: formData.description,
        url: formData.url,
        method: formData.method,
        key_value_pairs: keyValuePairs,
        header_key_value_pairs: headerKeyValuePairs,
      };
      onSave(dataSourceData);
    } catch {
      toast.error('Error processing key/value pairs.', toastConfig);
    }
  };

  const handleTestDataSource = () => {
    const formData = methods.getValues();

    const keyValuePairs = formData.key_value_pairs.reduce((acc, pair) => {
      if (pair.key) {
        acc[pair.key] = pair.value;
      }
      return acc;
    }, {});

    const headerKeyValuePairs = formData.header_key_value_pairs.reduce(
      (acc, pair) => {
        if (pair.key) {
          acc[pair.key] = pair.value;
        }
        return acc;
      },
      {}
    );

    const testData = {
      url: formData.url,
      method: formData.method,
      key_value_pairs: keyValuePairs,
      header_key_value_pairs: headerKeyValuePairs,
    };

    mutate(testData);
  };

  return (
    <DrawerWrapper
      toggle={onHide}
      title={dataSource ? 'Edit Custom Data Source' : 'Add Custom Data Source'}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(handleSaveDataSource)}
          className="d-flex flex-column justify-content-between flex-grow-1 custom-data-source-drawer"
        >
          <Offcanvas.Body>
            {/* Hidden ID Field */}
            {dataSource && <input type="hidden" {...register('id')} />}

            {/* Name Field with Required Validation */}
            <FormTextField
              title="Name"
              fieldName="name"
              register={register}
              rules={{ required: 'Name is required' }}
            />

            {/* Description Field */}
            <FormTextField
              title="Description"
              fieldName="description"
              register={register}
            />

            {/* URL Field with Required Validation */}
            <FormTextField
              title="URL"
              fieldName="url"
              register={register}
              rules={{
                required: 'URL is required',
                pattern: {
                  value: /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i,
                  message:
                    'Please enter a valid URL with http:// or https:// protocol',
                },
              }}
              helperText="Enter a full URL including http:// or https:// protocol (e.g., https://api.example.com/data)"
            />

            <div className="mb-3">
              <Form.Label>Method</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check
                  type="radio"
                  id="method-get"
                  label="GET"
                  value="GET"
                  {...register('method')}
                />
                <Form.Check
                  type="radio"
                  id="method-post"
                  label="POST"
                  value="POST"
                  {...register('method')}
                />
              </div>
              <Form.Text className="text-muted">
                Select the HTTP method to use when making requests to this data
                source.
              </Form.Text>
            </div>

            <FormKeyValuePairs
              title={
                watch('method') === 'GET'
                  ? 'Query Parameters'
                  : 'Body Parameters'
              }
              fieldName="key_value_pairs"
            />

            <FormKeyValuePairs
              title="Header Key/Value Pairs"
              fieldName="header_key_value_pairs"
              tooltip={
                <HelpTooltip
                  title="allowed-headers"
                  content={<HeadersToolTipInfo />}
                />
              }
            />

            {testResult && (
              <div className="mt-3">
                <div className="mb-2">
                  <strong>Status: </strong>
                  <span
                    className={
                      testResult.success ? 'text-success' : 'text-danger'
                    }
                  >
                    {testResult.success ? 'Success' : 'Failed'}
                  </span>
                  {!testResult.success && testResult.status && (
                    <span className="ms-2">(HTTP {testResult.status})</span>
                  )}
                </div>

                {testResult.error && (
                  <div className="mb-2">
                    <strong>Error: </strong>
                    <span className="text-danger">{testResult.error}</span>
                  </div>
                )}

                <div className="mb-2">
                  <strong>Response Data:</strong>
                  <div className="response-data-container">
                    {testResult.data ? (
                      <pre className="mb-0">
                        {JSON.stringify(testResult.data, null, 2)}
                      </pre>
                    ) : (
                      <div>No data returned</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Offcanvas.Body>

          {/* Drawer Footer with Action Buttons */}
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton className="button--secondary" onClick={onHide}>
                Cancel
              </StylishNewButton>
              <StylishNewButton
                onClick={handleTestDataSource}
                type="button"
                className="button--secondary"
                disabled={!watch('url') || isLoading}
              >
                {isLoading ? 'Testing...' : 'Test Connection'}
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={!isValid} // Disable if form is invalid
              >
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default CustomDataSourceEditDrawer;
